import styled from 'styled-components';

import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {media} from '~/shared/theme/media';

/* These two are loaded as URLs rather than `?react` components on purpose: svgr runs svgo, whose
   preset-default drops a viewBox that matches the width/height attributes. Both of these assets are
   authored that way, and without a viewBox the artwork stops scaling with the element - the curve
   flattens out and the stars stay at their mobile size. As <img> the assets are passed through
   untouched and keep their intrinsic ratio. */
import headerGradient from './assets/header-gradient.svg';
import headerStars from './assets/header-stars.svg';
import wallet3dIcon from './assets/wallet3dIcon.png';

const Root = styled.div`
  position: relative;
  height: 123px;
  ${media.minLargeMobile`
    height: 171px;
  `}
`;

/* Holds everything that has to stay inside the header: the gradient sheet and the stars are both
   taller than the strip we reveal, so they are cropped here. The wallet is deliberately a sibling -
   it hangs below the curve. */
const Decoration = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

/* The gradient keeps its own 500:275 ratio and is pinned to the bottom, so only its curved edge and
   the colours just above it are visible - the rest is cropped by Decoration, as in the design. */
const Gradient = styled(ImageWithAlt)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
`;

const StarsContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 1px;
  right: 1px;
  display: flex;
  justify-content: space-between;
  opacity: 0.5;
  ${media.minLargeMobile`
    top: 14px;
    left: 2px;
    right: 2px;
  `}
`;

const Stars = styled(ImageWithAlt)<{$mirrored?: boolean}>`
  transform: ${({$mirrored}) => ($mirrored ? 'scale(-1, 1)' : 'none')};
  ${media.minLargeMobile`
    width: 137.5px;
    height: 241.7px;
  `}

  body.contrast-filter & {
    display: none;
  }
`;

/* Overhangs the curve, so it is a sibling of Decoration rather than inside it. The design's box is
   144/160px, but wallet3dIcon.png is cropped tighter than that box, so these are sized up to land
   the illustration itself on the design's position. Re-exporting the asset means re-checking them. */
const WalletIcon = styled(ImageWithAlt)`
  position: absolute;
  top: 51px;
  left: 50%;
  transform: translateX(-50%);
  width: 138px;
  ${media.minLargeMobile`
    top: 58px;
    width: 154px;
  `}
`;

const ConvertLoyaltyBalanceHeader = () => (
  <Root>
    <Decoration aria-hidden="true">
      <Gradient src={headerGradient} noAlt />
      <StarsContainer>
        <Stars src={headerStars} $mirrored noAlt />
        <Stars src={headerStars} noAlt />
      </StarsContainer>
    </Decoration>
    <WalletIcon src={wallet3dIcon} noAlt />
  </Root>
);

export default ConvertLoyaltyBalanceHeader;
